<script setup lang="ts">
import { useHead } from '#imports'
import { useIdle } from '@vueuse/core'
import { computed, h, nextTick, onMounted, ref, watch } from 'vue'
import VoixVideoControls from './VoixVideoControls.vue'

// Config
const props = defineProps<{
  src: string
  options: {
    controls: {
      mode: 'cloudflare' | 'voix'
      display: boolean
      position: string
    }
    style: {
      respectHeight: boolean
      respectWidth: boolean
      aspectRatio: string
    }
    autoplay: boolean
    loop: boolean
    fullscreen: boolean
    volume: number
    mute: boolean
  }
  width: number
  height: number
}>()

// State
let player = null as any
const paused = ref(true)
const muted = ref(false)
const volume = ref(0)
const showControls = ref(false)

// Computed
const { idle, reset } = useIdle(1000)
const cloudflareId = computed(() => {
  const regex = /iframe\.cloudflarestream\.com\/([^/]+)/
  const match = props.src.match(regex)
  return match ? match[1] : undefined
})
const cloudflareControls = computed(() => {
  return props.options.controls.mode === 'cloudflare' && props.options.controls.display
})
const cloudflareAutoplay = computed(() => {
  return props.options.autoplay ? 'autoplay=true' : ''
})
const cloudflareMuted = computed(() => {
  return props.options.mute ? 'muted=true' : ''
})
const videoStyles = computed(() => {
  const styles = {
    aspectRatio: props.options.style.aspectRatio ? `aspect-ratio: ${props.options.style.aspectRatio}; ` : '',
    width: props.options.style.respectWidth ? `width: ${props.width}px; ` : '',
    height: props.options.style.respectHeight ? `height: ${props.height}px; ` : '',
  }

  return styles.aspectRatio + styles.width + styles.height
})

// Methods
function play() {
  paused.value = false
  player.play()
}
function pause() {
  paused.value = true
  player.pause()
}
function mute() {
  muted.value = true
  player.muted = true
}
function unMute() {
  muted.value = false
  player.muted = false
}
function setVolume({ value, init = false }: { value: number, init?: boolean }) {
  volume.value = value
  player.volume = value / 100

  if (!init && Number(value) === 0)
    mute()
  else
    unMute()
}
function fullscreen() {
  const iframe = document.getElementById('stream-player') as any

  if (iframe.requestFullscreen) {
    iframe.requestFullscreen()
  }
  else if (iframe.mozRequestFullScreen) {
    iframe.mozRequestFullScreen()
  }
  else if (iframe.webkitRequestFullscreen) {
    iframe.webkitRequestFullscreen()
  }
  else if (iframe.msRequestFullscreen) {
    iframe.msRequestFullscreen()
  }
}

// Watchers
watch(idle, (value) => {
  if (value) {
    showControls.value = false
  }
  else {
    showControls.value = true
  }
})

// Lifecycle
onMounted(async () => {
  // Wait For Cloudflare API
  const CloudflarePlayer = new Promise<void>((resolve) => {
    const checkCloudflare = () => {
      if (typeof Stream === 'function' && Stream(document.getElementById('stream-player')) !== null) {
        resolve()
      }
      else {
        setTimeout(checkCloudflare, 1000)
      }
    }
    checkCloudflare()
  })

  // Init Cloudflare Player
  await CloudflarePlayer.then(() => {
    const iframe = document.getElementById('stream-player') as any
    player = Stream(iframe)

    // Set Default Volume
    setVolume({ value: props.options.volume, init: true })

    // Set Default Mute
    if (props.options.mute)
      mute()
    if (props.options.autoplay)
      play()
  })
})

// Head
useHead({
  script: [
    {
      src: 'https://embed.cloudflarestream.com/embed/sdk.latest.js',
      async: true,
    },
  ],
})
</script>

<template>
  <div class="relative">
    <div :style="videoStyles" v-bind="$attrs">
      <iframe
        id="stream-player"
        :src="`https://iframe.cloudflarestream.com/${cloudflareId}?${cloudflareAutoplay}&${cloudflareMuted}&controls=${cloudflareControls}&loop=${options.loop}`"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowfullscreen="true"
        style="width: 100%; height: 100%; border: none;"
      />
    </div>

    <slot v-if="options.controls.mode === 'voix' && options.controls.display" :play="play" :pause="pause" :paused="paused" :mute="mute" :un-mute="unMute" :muted="muted" :volume="volume" :set-volume="setVolume" :fullscreen="fullscreen" :allow-fullscreen="options.fullscreen">
      <div
        class="flex absolute top-0 left-0 h-full w-full text-white duration-200"
        :class="{ 'opacity-0': !showControls, 'opacity-100': showControls || paused }"
        @mouseenter="showControls = true; reset()"
        @mousemove="showControls = true; reset()"
        @mouseleave="showControls = false"
      >
        <VoixVideoControls :paused="paused" :play="play" :pause="pause" :mute="mute" :un-mute="unMute" :muted="muted" :volume="volume" :set-volume="setVolume" :fullscreen="fullscreen" :allow-fullscreen="options.fullscreen" :controls-position="options.controls.position" />
      </div>
    </slot>
  </div>
</template>

<style scoped>
*{border-radius:0!important}
</style>
